/**
 * Booking-related API service functions
 */
import apiClient from './apiClient';
import { API_ENDPOINTS } from '../constants/api';

export const bookingService = {
  /**
   * Create a new booking
   * @param {Object} bookingData - Booking information
   * @returns {Promise} Booking creation result
   */
  createBooking: async (bookingData) => {
    return apiClient.post(API_ENDPOINTS.BOOKINGS.CREATE, bookingData);
  },

  /**
   * Get booking details by ID
   * @param {string} bookingId - Booking ID
   * @returns {Promise} Booking details
   */
  getBookingDetails: async (bookingId) => {
    return apiClient.get(`${API_ENDPOINTS.BOOKINGS.DETAILS}/${bookingId}`);
  },

  /**
   * Get user's booking history
   * @returns {Promise} List of bookings
   */
  getBookingHistory: async (params = {}) => {
    return apiClient.get(API_ENDPOINTS.BOOKINGS.HISTORY, params);
  },

  /**
   * Cancel a booking
   * @param {string} bookingId - Booking ID
   * @param {Object} cancelData - Cancellation details
   * @returns {Promise} Cancellation result
   */
  cancelBooking: async (bookingId, cancelData) => {
    return apiClient.post(`${API_ENDPOINTS.BOOKINGS.CANCEL}/${bookingId}`, cancelData);
  },

  /**
   * Get booking by reference number
   * @param {string} referenceNumber - Booking reference number
   * @returns {Promise} Booking details
   */
  getBookingByReference: async (referenceNumber) => {
    return apiClient.get(`${API_ENDPOINTS.BOOKINGS.BY_REFERENCE}/${referenceNumber}`);
  },

  /**
   * Get booking confirmation details
   * @param {string} bookingId - Booking ID
   * @returns {Promise} Booking confirmation data
   */
  getBookingConfirmation: async (bookingId) => {
    return apiClient.get(`/bookings/confirmation/${bookingId}`);
  },

  /**
   * Check for duplicate passenger bookings
   * @param {Array} passengers - Array of passenger objects with first_name, last_name, date_of_birth
   * @param {Array} flights - Array of flight objects with flight_number, departure_airport, arrival_airport, departure_date_time
   * @returns {Promise} Duplicate check result
   */
  checkDuplicateBooking: async (passengers, flights) => {
    return apiClient.post('/bookings/check-duplicate', { passengers, flights });
  },

  /**
   * Lock seats temporarily before payment
   * @param {Array} route_ids - Array of route IDs
   * @param {number} passenger_count - Number of passengers
   * @param {Object} booking_data - Full booking information
   * @returns {Promise} Lock result with booking_token
   */
  lockSeats: async (route_ids, passenger_count, booking_data, booking_token = null) => {
    return apiClient.post(API_ENDPOINTS.BOOKINGS.LOCK_SEATS, {
      route_ids,
      passenger_count,
      booking_data,
      booking_token
    });
  },

  /**
   * Revoke temporary seat lock
   * @param {string} booking_token - Booking token to revoke
   * @returns {Promise} Revocation result
   */
  revokeLock: async (booking_token) => {
    return apiClient.post(API_ENDPOINTS.BOOKINGS.REVOKE_LOCK, { booking_token });
  }
};

export default bookingService;
